import {combineLatest as observableCombineLatest, of as observableOf, Observable} from 'rxjs';

import {first, catchError, startWith, map, mergeMap, takeWhile} from 'rxjs/operators';
import {AbstractControlComponent} from '../abstract-control';
import {ViewTabComponent} from '../../viewtab.component';
import {ChangeDetectorRef, NgZone, Directive, Type, OnInit} from '@angular/core';
import {ErrorService} from '../../../../error/error.service';
import {CacheService} from '../../../../cache/cache.service';
import {ResourceService} from '../../../resource.service';
import {EnvironmentConfigService} from '../../../../core/services/environment-config.service';
import {MatAutocompleteSelectedEvent} from '@angular/material/autocomplete';
import {MatDialog} from '@angular/material/dialog';
import {AttributeFlowConstantDialog} from './attribute-flow-constant-dialog.component';
import {AttributeFlowCustomExpressionDialog} from './attribute-flow-custom-expression-dialog.component';
import {AttributeFlowFunctionDialog} from './attribute-flow-function-dialog.component';
import {COMMA, ENTER} from '@angular/cdk/keycodes';
import {AbstractControl, UntypedFormControl, ValidationErrors} from '@angular/forms';
import {
  filterAttributesFromDsml,
  parseAttributesFromManagementAgent,
} from './SynchronizationFunctions';
import * as convert from 'xml-js';
import {AttributeFlow} from './attribute-flow';

export interface AttributeFlowType extends Type<AttributeFlow> {
  createFromXml(xml: string): AttributeFlow;
}

@Directive()
export abstract class AbstractAttributeFlowControl
  extends AbstractControlComponent
  implements OnInit
{
  protected constructor(
    parent: ViewTabComponent,
    alternateName: string,
    private sourceType: 'ilm' | 'cs',
    private classRef: AttributeFlowType,
    changeDetector: ChangeDetectorRef,
    _ngZone: NgZone,
    errorService: ErrorService,
    private cacheService: CacheService,
    private resourceService: ResourceService,
    private dialog: MatDialog,
    private envConfigService: EnvironmentConfigService,
  ) {
    super(parent, alternateName, alternateName, changeDetector, _ngZone, errorService);
  }

  ngOnInit(): void {
    this._editEnabledSubject.pipe(takeWhile(() => this.alive)).subscribe(edit => {
      if (!edit) {
        this.incompleteFlows = [];
      }
    });
  }

  incompleteFlows: AttributeFlow[] = [];

  // raw XML of entries in the bound attribute that belong to the other flow direction
  // (in-and-outbound rules share PersistentFlow between the inbound and outbound control);
  // preserved verbatim and re-emitted by formatTarget so a write never drops them
  protected foreignFlowXml: string[] = [];

  ilmAttributes: Observable<string[]>;
  csAttributes: Observable<string[]>;

  separatorKeysCodes: number[] = [ENTER, COMMA];
  srcCtrl = new UntypedFormControl();
  filteredSourceOptions: Observable<{group: string; sources: SourceType[]}[]>;
  listViewTitle: Observable<string> = null;

  protected initValidators(): void {
    // disable default validators (don't call super)

    this.incompleteFlows = [];

    if (this._config.propertyExists('AttributeFlowTitle')) {
      this.listViewTitle = this._config.getPropertyValues('AttributeFlowTitle');
    } else if (this._config.propertyExists('ShowTitleBar')) {
      this.listViewTitle = this._config.getBooleanPropertyValues('ShowTitleBar').pipe(
        mergeMap((show: boolean) => {
          if (show === true) {
            return this._config.Caption.pipe(map(vo => (vo ? vo.Values : null)));
          } else {
            return observableOf(null);
          }
        }),
      );
    } else {
      this.listViewTitle = null;
    }

    const uniqueDestinationValidator = (control: AbstractControl): ValidationErrors | null => {
      const val = control.value;
      if (!Array.isArray(val)) return null;
      if (val.length < 2) return null;

      const destinations = val.map((x: AttributeFlow) => x.dest);
      if (new Set(destinations).size !== destinations.length) {
        return {uniqueDestination: control.value};
      } else {
        return null;
      }
    };
    const noIncompleteFlowsValidatior = (control: AbstractControl): ValidationErrors | null => {
      if (this.incompleteFlows.filter(flow => !flow.isEmpty()).length > 0) {
        return {incompleteFlows: true};
      } else {
        return null;
      }
    };
    this.formControl.setValidators([uniqueDestinationValidator, noIncompleteFlowsValidatior]);

    // initialize some variables

    this.csAttributes = this._config
      .getPropertyValues('ManagementAgent')
      .pipe(
        mergeMap(id =>
          parseAttributesFromManagementAgent(id, this.resourceService, this.envConfigService),
        ),
      );

    this.ilmAttributes = observableCombineLatest(
      this._config.getPropertyValues('MVSchema'),
      this._config.getPropertyValues('MVObjectType'),
      filterAttributesFromDsml,
    );

    const sourceAttributes = this.sourceType === 'ilm' ? this.ilmAttributes : this.csAttributes;

    this.filteredSourceOptions = observableCombineLatest(
      sourceAttributes,
      this.srcCtrl.valueChanges.pipe(startWith(null)),
      (attributes: string[], inputValue: string) => {
        const constantSources: SourceConstant[] = [
          new SourceConstant('Number'),
          new SourceConstant('String'),
        ];

        const advancedSources: SourceType[] = [new SourceCustomExpression(), new SourceFunction()];

        if (inputValue == null) {
          return [
            {
              group: 'Attribute',
              sources: attributes ? attributes.map(a => new SourceAttribute(a)) : [],
            },
            {
              group: 'Constant Value',
              sources: constantSources,
            },
            {
              group: 'Advanced',
              sources: advancedSources,
            },
          ];
        }

        if (typeof inputValue !== 'string') return [];

        const filterValue = inputValue.toLowerCase();
        return [
          {
            group: 'Attributes',
            sources: attributes
              ? attributes
                  .filter(a => a.toLowerCase().indexOf(filterValue) === 0)
                  .map(a => new SourceAttribute(a))
              : [],
          },
          {
            group: 'Constant',
            sources: constantSources.filter(
              src => src.displayName.toLowerCase().indexOf(filterValue) === 0,
            ),
          },
          {
            group: 'Advanced',
            sources: advancedSources.filter(
              src => src.displayName.toLowerCase().indexOf(filterValue) === 0,
            ),
          },
        ].filter(obj => obj.sources.length > 0);
      },
    );
  }

  protected static parseFunction(
    fnNode: any,
    source: SourceType[],
    attributes: string[],
    isRoot: boolean,
  ) {
    if (
      !Array.isArray(fnNode) ||
      fnNode.length !== 1 ||
      fnNode[0].$ === undefined ||
      !Array.isArray(source) ||
      !Array.isArray(attributes)
    ) {
      return;
    }
    const custom =
      typeof fnNode[0].$.isCustomExpression === 'string' &&
      fnNode[0].$.isCustomExpression.toLowerCase() === 'true';
    const args: SourceType[] = [];

    if (Array.isArray(fnNode[0].arg)) {
      fnNode[0].arg.forEach(argNode => {
        if (Array.isArray(argNode._text) && argNode._text.length >= 1) {
          const arg = argNode._text[0];
          if (attributes.indexOf(arg) !== -1) {
            args.push(new SourceAttribute(arg));
          } else if (arg[0] === '"' && arg[arg.length - 1] === '"') {
            args.push(new SourceConstant('String', arg.substring(1, arg.length - 1)));
          } else if (/^-?\d+$/.test(arg)) {
            args.push(new SourceConstant('Number', arg));
          } else {
            args.push(new SourceAttribute(arg));
            console.warn(
              'Argument "' +
                arg +
                '" is no string, number nor attribute. Assume attribute missing in list.',
            );
          }
        } else if (Array.isArray(argNode.fn)) {
          this.parseFunction(argNode.fn, args, attributes, false);
        }
      });
    }

    if (isRoot && fnNode[0].$.id === '+') {
      source.push(...args);
    } else {
      const name = fnNode[0].$.id;
      const conditionNames = [
        'Eq',
        'NotEquals',
        'LessThan',
        'GreaterThan',
        'LessThanOrEquals',
        'GreaterThanOrEquals',
        'IsPresent',
      ];
      let fn;
      if (conditionNames.indexOf(name) !== -1) {
        fn = new SourceCondition();
      } else {
        fn = new SourceFunction();
      }

      fn.functionName = name;
      fn.arguments = args;

      if (custom) {
        source.push(new SourceCustomExpression(fn));
      } else {
        source.push(fn);
      }
    }
  }

  delete(index: number): void {
    if (Array.isArray(this.formControl.value) && index < this.formControl.value.length) {
      this.formControl.value.splice(index, 1);
      this.formControl.updateValueAndValidity();
    } else {
      if (Array.isArray(this.formControl.value)) {
        index -= this.formControl.value.length;
      }
      if (index < this.incompleteFlows.length) {
        this.incompleteFlows.splice(index, 1);
      } else {
        console.error('Attribute Flow Control: Trying to delete invalid index!');
      }
    }
  }

  addRow(): void {
    this.incompleteFlows.push(new this.classRef());
  }

  get value(): AttributeFlow[] {
    return (
      [this.formControl.value, this.incompleteFlows]
        .filter(x => Array.isArray(x))
        .reduce((prev, cur) => prev.concat(cur)) ?? []
    );
  }

  srcRemoved(flow: AttributeFlow, srcIndex: number) {
    flow.src.splice(srcIndex, 1);
    this.formControl.updateValueAndValidity();
  }

  srcSelected(
    flow: AttributeFlow,
    event: MatAutocompleteSelectedEvent,
    inputField: HTMLInputElement,
  ): void {
    if (!Array.isArray(flow.src)) flow.src = [];
    this.handleSourceAdd(event.option.value)
      .pipe(
        catchError((err, caught) => this.handleError(err, caught)),
        first(),
      )
      .subscribe((added: SourceType) => {
        if (added != null) {
          flow.src.push(added);
          this.formControl.updateValueAndValidity();
          this.changeDetector.detectChanges();
        }
      });

    //can only be reset if an attribute was selected - otherwise still not defined
    if (inputField) inputField.value = '';
    this.srcCtrl.setValue(null);
  }

  private handleSourceAdd(source: SourceType): Observable<SourceType> {
    if (source instanceof SourceConstant) {
      const dlg = this.dialog.open(AttributeFlowConstantDialog);
      dlg.componentInstance.type = source.type;
      return dlg.afterClosed().pipe(
        map(value => {
          if (value == null || value === '') return null;
          return new SourceConstant(source.type, value);
        }),
      );
    } else if (source instanceof SourceCustomExpression) {
      const dlg = this.dialog.open(AttributeFlowCustomExpressionDialog);
      return dlg.afterClosed().pipe(
        map(value => {
          if (value == null || value === '') return null;
          return value;
        }),
      );
    } else if (source instanceof SourceFunction) {
      const dlg = this.dialog.open(AttributeFlowFunctionDialog, {disableClose: true});
      dlg.componentInstance.sourceAttributes =
        this.sourceType === 'ilm' ? this.ilmAttributes : this.csAttributes;
      return dlg.afterClosed();
    } else {
      return observableOf(source);
    }
  }

  srcClick(source: SourceType): void {
    if (source instanceof SourceConstant) {
      const dlg = this.dialog.open(AttributeFlowConstantDialog);
      dlg.componentInstance.type = source.type;
      dlg.componentInstance.value = source.value;
      dlg
        .afterClosed()
        .pipe(
          catchError((err, caught) => this.handleError(err, caught)),
          first(),
        )
        .subscribe(value => {
          if (value == null || value === '') return;
          source.value = value;
          this.formControl.updateValueAndValidity();
        });
    } else if (source instanceof SourceCustomExpression) {
      const dlg = this.dialog.open(AttributeFlowCustomExpressionDialog);
      dlg.componentInstance.customExpression = source;
      dlg
        .afterClosed()
        .pipe(
          catchError((err, caught) => this.handleError(err, caught)),
          first(),
        )
        .subscribe((value: SourceCustomExpression) => {
          if (value == null) return;
          source.customFunction = value.customFunction;
          this.formControl.updateValueAndValidity();
        });
    } else if (source instanceof SourceFunction) {
      const dlg = this.dialog.open(AttributeFlowFunctionDialog, {disableClose: true});
      dlg.componentInstance.sourceAttributes =
        this.sourceType === 'ilm' ? this.ilmAttributes : this.csAttributes;
      dlg.componentInstance.function = source;
      dlg
        .afterClosed()
        .pipe(
          catchError((err, caught) => this.handleError(err, caught)),
          first(),
        )
        .subscribe(value => {
          if (value != null) {
            this.formControl.updateValueAndValidity();
          }
        });
    } else {
      // do nothing
    }
  }

  srcClickable(source: SourceType): boolean {
    return (
      source instanceof SourceConstant ||
      source instanceof SourceCustomExpression ||
      source instanceof SourceFunction
    );
  }

  static splitFlowXml(
    source: string[],
    classRef: AttributeFlowType,
  ): {ownFlows: AttributeFlow[]; foreignXml: string[]} {
    const ownFlows: AttributeFlow[] = [];
    const foreignXml: string[] = [];
    source.forEach(xml => {
      let flow: AttributeFlow = null;
      try {
        flow = classRef.createFromXml(xml);
      } catch (e) {
        console.warn('Could not parse xml of attribute flow', xml, e);
      }
      if (flow != null) {
        ownFlows.push(flow);
      } else {
        // other flow direction of a shared attribute (or unparsable) - preserve raw xml
        foreignXml.push(xml);
      }
    });
    return {ownFlows, foreignXml};
  }

  static mergeFlowXml(flows: AttributeFlow[], foreignXml: string[]): string[] {
    const xml = flows
      .filter(flow => flow != null)
      .map(flow => flow.generateXml())
      .concat(foreignXml);
    return xml.length === 0 ? null : xml;
  }

  protected formatSource(source: any): any {
    // reset before the early return so a control never carries stale foreign data
    this.foreignFlowXml = [];
    if (source == null || !Array.isArray(source) || source.length === 0) {
      if (this.incompleteFlows.length === 0) {
        this.addRow();
      }

      return [];
    }

    const {ownFlows, foreignXml} = AbstractAttributeFlowControl.splitFlowXml(source, this.classRef);
    this.foreignFlowXml = foreignXml;
    if (ownFlows.length === 0 && this.incompleteFlows.length === 0) {
      this.addRow();
    }
    return ownFlows;
  }

  protected formatTarget(value: any): any {
    let newIncompleteFlows: AttributeFlow[] = [];
    if (!Array.isArray(value)) {
      value = [];
    } else {
      const newValue = [];
      value.forEach((flow: AttributeFlow) => {
        if (flow == null) {
          return;
        }
        if (flow.isComplete()) {
          newValue.push(flow);
        } else {
          newIncompleteFlows.push(flow);
        }
      });
      value = newValue;
    }

    let incompleteIndicesToRemove: number[] = [];
    for (let i = 0; i < this.incompleteFlows.length; i++) {
      if (this.incompleteFlows[i].isComplete()) {
        value.push(this.incompleteFlows[i]);
        incompleteIndicesToRemove.push(i);
      }
    }
    incompleteIndicesToRemove.reverse().forEach(i => {
      this.incompleteFlows.splice(i, 1);
    });

    this.incompleteFlows.unshift(...newIncompleteFlows);

    return AbstractAttributeFlowControl.mergeFlowXml(value, this.foreignFlowXml);
  }

  static buildSourceXml(val: AttributeFlow, xmlObj: any) {
    if (Array.isArray(val.src) && val.src.length > 0) {
      if (val.src.length === 1 && val.src[0] instanceof SourceConstant) {
        const src: SourceConstant = <SourceConstant>val.src[0];
        xmlObj.src = src.value;
        return;
      }

      if (val.src.length > 1) {
        xmlObj.fn = {
          $: {
            id: '+',
            isCustomExpression: false,
          },
          arg: [],
        };
      }

      val.src.forEach((src: SourceType) => {
        if (src instanceof SourceAttribute) {
          xmlObj.src.attr.push(src.name);
          if (xmlObj.fn != null) {
            xmlObj.fn.arg.push(src.name);
          }
        } else if (src instanceof SourceConstant) {
          let arg;
          if (src.type === 'String') {
            arg = '"' + src.value + '"'; // value isn't escaped by old portal
          } else {
            arg = src.value;
          }
          xmlObj.fn.arg.push(arg);
        } else if (src instanceof SourceCustomExpression) {
          const newAttributes: string[] = [];
          if (src.customFunction != null) {
            const arg = this.buildFunctionXml(src.customFunction, newAttributes, true);
            xmlObj.src.attr.push(...newAttributes);
            if (xmlObj.fn == null) {
              xmlObj.fn = arg.fn;
            } else {
              xmlObj.fn.arg.push(arg);
            }
          }
        } else if (src instanceof SourceFunction) {
          const newAttributes: string[] = [];
          const arg = this.buildFunctionXml(src, newAttributes);
          xmlObj.src.attr.push(...newAttributes);
          if (xmlObj.fn == null) {
            xmlObj.fn = arg.fn;
          } else {
            xmlObj.fn.arg.push(arg);
          }
        }
      });
    }
  }

  private static buildFunctionXml(
    src: SourceFunction | SourceCondition,
    newAttributes: string[],
    isCustom: boolean = false,
  ): {fn: {$: any; arg: string[]}} {
    const obj = {
      fn: {
        $: {
          id: src.functionName,
          isCustomExpression: isCustom,
        },
        arg: [],
      },
    };

    if (Array.isArray(src.arguments)) {
      src.arguments.forEach((fnArg: SourceType) => {
        if (fnArg instanceof SourceAttribute) {
          obj.fn.arg.push(fnArg.name);
          newAttributes.push(fnArg.name);
        } else if (fnArg instanceof SourceConstant) {
          if (fnArg.type === 'String') {
            obj.fn.arg.push('"' + fnArg.value + '"');
          } else {
            obj.fn.arg.push(fnArg.value);
          }
        } else if (fnArg instanceof SourceCustomExpression) {
          const newInnerAttributes: string[] = [];
          if (fnArg.customFunction != null) {
            const innerArg = this.buildFunctionXml(fnArg.customFunction, newInnerAttributes, true);
            newAttributes.push(...newInnerAttributes);
            obj.fn.arg.push(innerArg);
          }
        } else if (fnArg instanceof SourceFunction || fnArg instanceof SourceCondition) {
          const newInnerAttributes: string[] = [];
          const innerArg = this.buildFunctionXml(fnArg, newInnerAttributes);
          newAttributes.push(...newInnerAttributes);
          obj.fn.arg.push(innerArg);
        }
      });
    }

    if (obj.fn.arg.length === 0) {
      delete obj.fn.arg;
    }

    return obj;
  }

  static parseSourceXml(
    root: {src: string | any[]; fn: string | any[]},
    flow: AttributeFlow,
  ): void {
    const attributes: string[] = [];

    if (Array.isArray(root.src) && root.src.length >= 1) {
      if (Array.isArray(root.src[0].attr)) {
        root.src[0].attr.forEach(attrXml => {
          if (Array.isArray(attrXml._text) && attrXml._text.length >= 1) {
            attributes.push(attrXml._text[0]);
          }
        });
      } else if (Array.isArray(root.src[0]._text) && root.src[0]._text.length >= 1) {
        // single constant
        const value = root.src[0]._text[0];
        if (/^-?\d+$/.test(value)) {
          flow.src = [new SourceConstant('Number', value)];
        } else {
          flow.src = [new SourceConstant('String', value)];
        }
        return;
      }
    }

    if (Array.isArray(root.fn) && root.fn.length === 1) {
      this.parseFunction(root.fn, flow.src, attributes, true);
    } else {
      flow.src = attributes.map(a => new SourceAttribute(a));
    }
  }

  static convertXmlToJs(xml: string): any {
    return convert.xml2js(xml, {
      compact: true,
      alwaysArray: true,
      attributesKey: '$',
    });
  }

  static convertJsToXml(obj: any): string {
    return convert
      .js2xml(obj, {
        compact: true,
        attributesKey: '$',
        fullTagEmptyElementFn: (currentElementName, currentElementObj) =>
          currentElementName === 'scoping',
      })
      .replace(/"\/>/g, '" />');
  }
}

export abstract class SourceType {
  protected constructor(public displayName: string) {}

  public abstract clone(): SourceType;

  public get summary(): string {
    return this.displayName;
  }
}

export class SourceAttribute extends SourceType {
  constructor(public name: string) {
    super(name);
  }

  clone(): SourceAttribute {
    return this; // no clone needed
  }
}

export class SourceConstant extends SourceType {
  constructor(
    public type: 'Number' | 'String',
    public value?: string,
  ) {
    super('Constant ' + type);
  }

  clone(): SourceConstant {
    return new SourceConstant(this.type, this.value);
  }

  get summary(): string {
    return this.type === 'Number' ? this.value : '"' + this.value + '"';
  }
}

export class SourceCustomExpression extends SourceType {
  constructor(public customFunction?: SourceFunction | SourceCondition) {
    super('Custom Expression');
  }

  clone(): SourceCustomExpression {
    return new SourceCustomExpression(this.customFunction);
  }

  get summary(): string {
    if (this.customFunction) {
      return this.customFunction.summary;
    } else {
      return '';
    }
  }
}

export class SourceFunction extends SourceType {
  public functionName:
    | 'BitAnd'
    | 'BitOr'
    | 'ConvertSidToString'
    | 'ConvertStringToGuid'
    | 'CRLF'
    | 'DateTimeFormat'
    | 'IIF'
    | 'IsPresent'
    | 'Left'
    | 'LeftPad'
    | 'LowerCase'
    | 'LTrim'
    | 'Mid'
    | 'Null'
    | 'ProperCase'
    | 'RandomNum'
    | 'ReplaceString'
    | 'Right'
    | 'RightPad'
    | 'RTrim'
    | 'Trim'
    | 'UpperCase'
    | 'Word'
    | '+';
  public arguments: SourceType[] = [];

  constructor() {
    super('Function');
  }

  clone(): SourceFunction {
    const clone = new SourceFunction();
    clone.functionName = this.functionName;
    clone.arguments = this.arguments;
    return clone;
  }

  get summary(): string {
    if (this.functionName === '+') {
      return this.arguments.map(a => a.summary).join(' + ');
    } else {
      return this.functionName + '(' + this.arguments.map(a => a.summary).join(', ') + ')';
    }
  }
}

export class SourceCondition extends SourceType {
  public functionName:
    | 'Eq'
    | 'NotEquals'
    | 'LessThan'
    | 'GreaterThan'
    | 'LessThanOrEquals'
    | 'GreaterThanOrEquals'
    | 'IsPresent';
  public arguments: SourceType[] = [];

  constructor() {
    super('Condition');
  }

  clone(): SourceCondition {
    const clone = new SourceCondition();
    clone.functionName = this.functionName;
    clone.arguments = this.arguments;
    return clone;
  }

  get summary(): string {
    return this.functionName + '(' + this.arguments.map(a => a.summary).join(', ') + ')';
  }
}
